export type Commit = {
  sha: string;
  message: string;
  author: string;
  date: string;
  url: string;
  additions?: number;
  deletions?: number;
  filesChanged?: number;
  billed?: boolean;
  billingAmount?: number;
  milestoneId?: string;
  milestoneTitle?: string;

  workDate?: string;
  workHours?: number;

  workStatus?: 'unassigned' | 'assigned' | 'completed';
  workorderId?: string;
};

export type TimeEntry = {
  id: string;
  date: string;
  hours: number;
  hourlyRate?: number;
  description: string;
  billable: boolean;

  // NOTE: Backwards compatible. Older data has no kind and is treated as a git order.
  kind?: 'git-order' | 'time-ticket';

  // For time tickets, optionally link the ticket to a Git order.
  gitOrderId?: string | null;

  stage?: 'workorder' | 'invoiced';
  commitShas?: string[];
  commitRate?: number;
  misc?: number;
  taxes?: number;
  invoiceJournalEntryId?: string | null;
};

export type Expense = {
  id: string;
  date: string;
  category: string;
  amount: number;
  misc?: number;
  taxes?: number;
  notes?: string;
  journalEntry?: string;
  attachmentName?: string;
  attachmentType?: string;
};

export const INTERNAL_CUSTOMER_ID = 'internal';
export const INTERNAL_CUSTOMER_NAME = 'Internal';

export type Customer = {
  id: string;
  name: string;
  kind?: 'external' | 'internal';
};

export type JournalEntry = {
  id: string;
  date: string;
  entryType: 'retainer' | 'service-charge' | 'initialization-webapp-build';
  customerId?: string | null;
  customer: string;
  amount: number;
  quantity?: number;
  unitRate?: number;
  misc?: number;
  taxes?: number;
  notes?: string;
  createdBy?: string;
};

export const formatJournalEntryType = (entryType: JournalEntry['entryType']) => {
  if (entryType === 'service-charge') return 'Service charge';
  if (entryType === 'initialization-webapp-build') return 'Initialization WebApp Build';
  return 'Retainer';
};

export type InvoiceDraft = {
  id: string;
  date: string;
  entryType: JournalEntry['entryType'];
  customerId?: string | null;
  customer: string;
  amount: string;
  quantity?: string;
  unitRate?: string;
  misc: string;
  taxes: string;
  notes: string;
};

export type RepoFile = {
  path: string;
  content: string;
  size: number;
  updatedAt: string;
};

export type CodeDownloadLog = {
  id: string;
  downloadedAt: string;
  reason: string;
  version: string;
  commitSha: string | null;
  actorId: string | null;
  fileCount: number | null;
};

export type HostingDeployment = {
  id: string;
  commitSha: string;
  buildTimeUtc: string | null;
  firstSeenAt: string;
  hostingOrigin: string | null;
  recordedByActorId: string | null;
};

export type Milestone = {
  id: string;
  title: string;
  createdAt: string;
  toCommitSha: string;
  fromCommitSha: string | null;
  commitCount: number;
  snapshotVersion: string | null;
  state: 'open' | 'closed';
  description: string | null;
  dueOn: string | null;
  github: {
    number: number | null;
    htmlUrl: string | null;
    apiUrl: string | null;
  };
};

export type DeveloperSnapshot = {
  commits?: Commit[];
  commitCount?: number;
  totalAdditions?: number;
  totalDeletions?: number;
  totalStatsCommitCount?: number;
  totalsUpdatedAt?: string;
  commitsUpdatedAt?: string;
  lastCommitSyncAt?: string;
  timeEntries?: TimeEntry[];
  expenses?: Expense[];
  retainerPaid?: number;
  ratePerCommit?: number;
  repoOwner?: string;
  repoName?: string;
  repoFiles?: RepoFile[];
  repoFilesStorage?: 'doc' | 'subcollection';
  repoFileCount?: number;
  repoFilesUpdatedAt?: string;
  repoSnapshotCommitSha?: string;
  repoSnapshotVersion?: string;
  customers?: Customer[];
  journalEntries?: JournalEntry[];
};
